import { useCallback, useEffect, useRef, useState } from "react";
import { transcribeAssistantAudio } from "api";
import useLiveMicVisualizer from "./useLiveMicVisualizer";

const MIN_KEYBOARD_SUBMIT_MS = 900;

const getSupportedAudioMimeType = () => {
  if (typeof MediaRecorder === "undefined") return "";

  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/mpeg",
  ];

  return (
    candidates.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) || ""
  );
};

const isRecordingSupported = () =>
  typeof window !== "undefined" &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof MediaRecorder !== "undefined";

/**
 * Encapsulates microphone capture, recording and backend transcription so it can
 * be shared between the floating assistant bubble and the chat composer. The only
 * thing that differs between consumers is what happens with the final transcript,
 * which is handled via the `onTranscript` callback.
 */
const useMicRecording = ({ onTranscript, onError } = {}) => {
  const [micState, setMicState] = useState("idle");
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingStartedAtRef = useRef(0);
  const cancelRecordingRef = useRef(false);
  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);

  onTranscriptRef.current = onTranscript;
  onErrorRef.current = onError;

  const isListening = micState === "listening";

  const {
    micOrbRef,
    start: startLiveVisualizer,
    stop: stopLiveVisualizer,
    waveformCanvasRef,
  } = useLiveMicVisualizer();

  const reportError = useCallback((message) => {
    onErrorRef.current?.(message);
  }, []);

  const stopMediaStream = useCallback(() => {
    stopLiveVisualizer();
    mediaStreamRef.current?.getTracks?.().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  }, [stopLiveVisualizer]);

  useEffect(() => {
    if (!isListening || !mediaStreamRef.current) return undefined;

    startLiveVisualizer(mediaStreamRef.current);

    return () => {
      stopLiveVisualizer();
    };
  }, [isListening, startLiveVisualizer, stopLiveVisualizer]);

  useEffect(() => {
    return () => {
      cancelRecordingRef.current = true;
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      stopMediaStream();
    };
  }, [stopMediaStream]);

  const transcribeRecording = useCallback(
    async ({ audioBlob, durationMs }) => {
      setMicState("transcribing");

      try {
        const { transcript } = await transcribeAssistantAudio({
          audioBlob,
          durationMs,
        });

        setMicState("idle");
        onTranscriptRef.current?.(transcript);
      } catch (error) {
        const message =
          error?.message ||
          "I could not transcribe that recording. Please try again.";
        setMicState("idle");
        reportError(message);
      }
    },
    [reportError],
  );

  const startListening = useCallback(async () => {
    if (!isRecordingSupported()) {
      reportError("This browser does not support microphone recording.");
      return;
    }

    try {
      cancelRecordingRef.current = false;
      audioChunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedAudioMimeType();
      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined,
      );

      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      recordingStartedAtRef.current = Date.now();

      recorder.ondataavailable = (event) => {
        if (event.data?.size) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        stopMediaStream();
        setMicState("idle");
        reportError("Recording failed. Please try again.");
      };

      recorder.onstop = () => {
        const chunks = audioChunksRef.current;
        const durationMs = Date.now() - recordingStartedAtRef.current;
        const wasCancelled = cancelRecordingRef.current;
        const recordingType = recorder.mimeType || mimeType || "audio/webm";

        stopMediaStream();
        mediaRecorderRef.current = null;
        audioChunksRef.current = [];

        if (wasCancelled) {
          setMicState("idle");
          return;
        }

        const audioBlob = new Blob(chunks, { type: recordingType });
        if (!audioBlob.size) {
          setMicState("idle");
          reportError("No audio was recorded. Please try again.");
          return;
        }

        transcribeRecording({ audioBlob, durationMs });
      };

      recorder.start();
      setMicState("listening");
    } catch (error) {
      stopMediaStream();
      setMicState("idle");
      const message =
        error?.name === "NotAllowedError"
          ? "Microphone permission was denied."
          : "Could not start microphone recording.";
      reportError(message);
    }
  }, [reportError, stopMediaStream, transcribeRecording]);

  const stopListening = useCallback(
    ({ requireMinimumDuration = false } = {}) => {
      if (mediaRecorderRef.current?.state !== "recording") return;
      if (
        requireMinimumDuration &&
        Date.now() - recordingStartedAtRef.current < MIN_KEYBOARD_SUBMIT_MS
      ) {
        return;
      }

      cancelRecordingRef.current = false;
      setMicState("transcribing");
      mediaRecorderRef.current.stop();
    },
    [],
  );

  const cancelListening = useCallback(() => {
    cancelRecordingRef.current = true;

    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
      return;
    }

    stopMediaStream();
    setMicState("idle");
  }, [stopMediaStream]);

  return {
    micState,
    startListening,
    stopListening,
    cancelListening,
    isSupported: isRecordingSupported(),
    waveformCanvasRef,
    micOrbRef,
  };
};

export default useMicRecording;
